import React, { useState, useRef } from 'react';
import { Building2 } from 'lucide-react';
import { adminService, ImportResult, ExportOptions } from '../../services/adminService';
import { ImportExportRow, IncludeKey, transferMeta, useTransferLog } from './ImportExportRow';

export const OrganizationImportExport: React.FC = () => {
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importFormat, setImportFormat] = useState<'csv' | 'excel'>('excel');
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('excel');
  const [exportStatus, setExportStatus] = useState<string[]>([]);
  const [includeAssignments, setIncludeAssignments] = useState(false);
  const [includeInfluence, setIncludeInfluence] = useState(false);
  const [includeWiki, setIncludeWiki] = useState(false);
  const [includeMedia, setIncludeMedia] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const log = useTransferLog('organizations');

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setImportFile(file);
      setError(null);
      setImportResult(null);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      const result = await adminService.importOrganizations(importFile, importFormat);
      setImportResult(result);
      
      if (result.success) {
        log.markImport(result.imported);
        setImportFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    } catch (err) {
      setError('Import failed. Please try again.');
      console.error('Import error:', err);
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError(null);

    try {
      const options: ExportOptions = {
        format: exportFormat,
        status: exportStatus.length > 0 ? exportStatus : undefined,
        includeAssignments,
        includeInfluence,
        includeWiki,
        includeMedia,
      };

      const blob = await adminService.exportOrganizations(options);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organizations-${new Date().toISOString().split('T')[0]}.${exportFormat === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      log.markExport();
    } catch (err) {
      setError('Export failed. Please try again.');
      console.error('Export error:', err);
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadTemplate = async (format: 'csv' | 'excel') => {
    try {
      const blob = await adminService.downloadOrganizationTemplate(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `organizations-template.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Template download failed. Please try again.');
      console.error('Template download error:', err);
    }
  };

  const handleStatusChange = (status: string, checked: boolean) => {
    if (checked) {
      setExportStatus([...exportStatus, status]);
    } else {
      setExportStatus(exportStatus.filter(s => s !== status));
    }
  };

  const include: Record<IncludeKey, boolean> = {
    assignments: includeAssignments,
    influence: includeInfluence,
    wiki: includeWiki,
    media: includeMedia,
  };

  const handleIncludeChange = (key: IncludeKey, checked: boolean) => {
    if (key === 'assignments') setIncludeAssignments(checked);
    if (key === 'influence') setIncludeInfluence(checked);
    if (key === 'wiki') setIncludeWiki(checked);
    if (key === 'media') setIncludeMedia(checked);
  };

  return (
    <ImportExportRow
      id="organizations-data"
      icon={Building2}
      kind="organization"
      label="Organizations"
      noun="organizations"
      meta={transferMeta(log.lastExport, log.lastImport)}
      fileInputRef={fileInputRef}
      importFile={importFile}
      onFileSelect={handleFileSelect}
      importFormat={importFormat}
      onImportFormatChange={setImportFormat}
      importing={importing}
      onImport={handleImport}
      onDownloadTemplate={handleDownloadTemplate}
      importResult={importResult}
      exportFormat={exportFormat}
      onExportFormatChange={setExportFormat}
      exportStatus={exportStatus}
      onStatusChange={handleStatusChange}
      include={include}
      onIncludeChange={handleIncludeChange}
      exporting={exporting}
      onExport={handleExport}
      error={error}
    />
  );
};
